<template lang="pug">
  modal(:title='$t("kreaContentImageModal.title")' @closeModal='closeModal' maxWidth='640px')
    .width-100.m-top-2(v-show='!file')
      .width-100(@drop.prevent='dropFile' @dragenter.prevent @dragover.prevent)
        label.input-area.column.middle.center.m-bottom-1
          input.input-hidden(type='file' @change='selectFile' ref='kreaFileInput' accept='image/png, image/jpeg, image/jpg')
          simple-svg.gray-icon.m-bottom-1(:filepath='imageIcon' width='30px' height='30px')
          h2.text-center.text-big.blue.m-bottom-05 {{ $t("krea.kreaFileUploader.image.selectFile") }}
          h2.text-center.text-normal.dark-gray {{ $t("krea.kreaFileUploader.image.dropFile") }}
    .width-100.m-top-2.relative(v-show='file && !images.header')
      h2.text-normal.m-bottom-1 {{ $t("kreaContentImageModal.cropHeader") }}
      cropper.header-cropper(
        ref='headerCropper'
        :src='file'
        :stencil-props='headerStencilProps'
        :canvas='{width: 1920, height: 300}'
      )
      .row.middle.space-between.m-top-2
        button.secondary-button(@click='closeModal') {{ $t("kreaContentImageModal.cancel") }}
        button.primary-button(@click='saveHeaderImage') {{ $t("kreaContentImageModal.next") }}
    .width-100.m-top-2.relative(v-show='images.header && !images.thumbnail')
      h2.text-normal.m-bottom-1 {{ $t("kreaContentImageModal.cropThumbnail") }}
      cropper.header-cropper(
        ref='thumbnailCropper'
        :src='file'
        :stencil-props='thumbnailStencilProps'
        :canvas='{width: 250, height: 250}'
      )
      .row.middle.space-between.m-top-2
        button.secondary-button(@click='backToHeader') {{ $t("kreaContentImageModal.back") }}
        button.primary-button(@click='saveThumbnailImage') {{ $t("kreaContentImageModal.uploadImages") }}
    .width-100.loader(v-show='uploading')
      .input-area.loading.column.middle.center.m-bottom-1
        .row.middle.center.relative.spinner-container
          .spinner.m-bottom-05.row.middle.center
          h2.spinner-text.text-small {{ totalProgress + '%' }}
        h2.text-center.text-big.blue.m-bottom-1 {{ $t(`krea.kreaFileUploader.uploadingFile`) }}
        button.small-button.secondary-button(@click='cancelUpload') {{ $t(`krea.kreaFileUploader.cancel`) }}
</template>

<script>
import Modal from '@/components/Modal'
import imageIcon from '@/assets/krea/ic_image.svg'
import { mapActions, mapState } from 'vuex'
import { getBase64 } from '../../../utils/file'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { uploadKreaContentFile } from '../../../services/FileUploadService'
import Axios from 'axios'
import 'vue-advanced-cropper/dist/theme.classic.css'

export default {
  components: { Modal, Cropper },
  data() {
    return {
      imageIcon,
      file: null,
      fileName: null,
      images: {
        header: null,
        thumbnail: null
      },
      uploading: false,
      fileController: Axios.CancelToken.source(),
      headerProgress: 0,
      thumbnailProgress: 0
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.userInfo.userInfo,
      content: state => state.kreaEducationalContent.content
    }),
    headerStencilProps() {
      return {
        movable: true,
        resizable: true,
        aspectRatio: 1920 / 300
      }
    },
    thumbnailStencilProps() {
      return {
        movable: true,
        resizable: true,
        aspectRatio: 1
      }
    },
    totalProgress() {
      return (Math.round(this.headerProgress / 2)) + (Math.round(this.thumbnailProgress / 2))
    }
  },
  methods: {
    ...mapActions([
      'hideElement',
      'editKreaContentField'
    ]),
    async selectFile(evt) {
      const file = evt.target.files[0]
      this.fileName = file.name
      const encoded = await getBase64(file)
      this.file = `data:${file.type};base64,${encoded}`
      this.$refs.kreaFileInput.value = null
    },
    backToHeader() {
      this.images.header = null
      this.$refs.headerCropper.refresh()
    },
    saveHeaderImage() {
      const { canvas } = this.$refs.headerCropper.getResult()
      if (canvas) {
        const dataUrl = canvas.toDataURL('image/png')
        this.images.header = dataUrl
        this.$refs.thumbnailCropper.refresh()
      }
    },
    saveThumbnailImage() {
      const { canvas } = this.$refs.thumbnailCropper.getResult()
      if (canvas) {
        const dataUrl = canvas.toDataURL('image/png')
        this.images.thumbnail = dataUrl
        this.uploadImages()
      }
    },
    updateProgress(event, progress) {
      const totalLength = event.lengthComputable
        ? event.total
        : event.target.getResponseHeader('content-length') || event.target.getResponseHeader('x-decompressed-content-length')
      if (totalLength) {
        this[progress] = Math.round((event.loaded * 100) / totalLength)
      }
    },
    cancelUpload() {
      this.headerProgress = 0
      this.thumbnailProgress = 0
      this.uploading = false
      this.fileController.cancel()
    },
    async uploadImages() {
      const { schoolGroupId, schoolGroupName } = this.userInfo
      this.uploading = true
      const headerResponse = await uploadKreaContentFile({
        file: this.images.header.match(/base64(.)*/)[0].replace('base64,', ''),
        fileName: this.fileName.replace(/\.[^/.]+$/, '') + '_picture',
        contentId: this.content.id,
        extension: 'png',
        schoolGroupId,
        schoolGroupName
      }, {
        onUploadProgress: (evt) => this.updateProgress(evt, 'headerProgress'),
        cancelToken: this.fileController.token
      })
      const thumbnailResponse = await uploadKreaContentFile({
        file: this.images.thumbnail.match(/base64(.)*/)[0].replace('base64,', ''),
        fileName: this.fileName.replace(/\.[^/.]+$/, '') + '_thumbnail',
        contentId: this.content.id,
        extension: 'png',
        schoolGroupId,
        schoolGroupName
      }, {
        onUploadProgress: (evt) => this.updateProgress(evt, 'thumbnailProgress'),
        cancelToken: this.fileController.token
      })
      this.uploading = false
      this.editKreaContentField({ field: 'picture', value: headerResponse.data })
      this.editKreaContentField({ field: 'thumbnail', value: thumbnailResponse.data })
      this.closeModal()
    },
    closeModal() {
      this.hideElement({ element: 'kreaContentImageModal' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.header-cropper{
  width: 640px;
  height: 350px;
}

.input-hidden{
  display: none;
}

.input-area{
  cursor: pointer;
  height: 140px;
  border: 1px dashed $gray;
  border-radius: 10px;
  &.loading{
    cursor: default;
  }
}

.spinner-text{
  position: absolute;
  left: 50%;
  top: 30%;
  transform: translateX(-50%);
}

.spinner{
  width: 50px;
  height: 50px;
  padding: 5px;
  border: 4px solid transparent;
  border-top: 4px solid $blue;
  border-left: 4px solid $blue;
  border-radius: 50%;
  animation: loading 1s linear infinite;
}

@keyframes loading {
  from{
    transform: rotate(0);
  }
  to{
    transform: rotate(360deg);
  }
}
</style>
